import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Componente LightShowMobile (Aficionado)
 * Gestiona la sincronización de flashes y pantallas usando manipulación global del DOM
 * y sincronización de reloj por diferencia de latencia (estilo SNTP).
 */
const LightShow = ({ config, roomId, onExit }) => {
    // ==========================================
    // ESTADOS Y REFERENCIAS
    // ==========================================
    const [hasGrantedPermissions, setHasGrantedPermissions] = useState(false);
    const [permissionError, setPermissionError] = useState(null);
    const [activeBgColor, setActiveBgColor] = useState('');
    const [timeOffset, setTimeOffset] = useState(0);
    const [isSyncing, setIsSyncing] = useState(true);
    const [activeConfig, setActiveConfig] = useState(config);

    // Sincronizar activeConfig si config por prop cambia
    useEffect(() => {
        if (config) {
            setActiveConfig(config);
        }
    }, [config]);

    // Manejo de late join y estados perdidos al montar el componente (con blindaje de carrera)
    useEffect(() => {
        let isCurrent = true;
        const fetchCurrentState = async () => {
            if (activeConfig && activeConfig.isRunning) return;
            try {
                const url = roomId 
                    ? `${process.env.REACT_APP_API_URL}LightShow/current-state?roomId=${roomId}`
                    : `${process.env.REACT_APP_API_URL}LightShow/current-state`;
                const response = await fetch(url);
                if (response.ok && isCurrent) {
                    const data = await response.json();
                    if (data && data.isRunning && data.config) {
                        console.log("[LightShow] Sincronización tardía exitosa desde el endpoint.");
                        setActiveConfig(prev => {
                            if (prev && prev.isRunning) return prev; // Evitar pisar por websocket
                            return {
                                ...data.config,
                                isRunning: true,
                                serverStartTimestampMilliseconds: data.serverStartTimestampMilliseconds
                            };
                        });
                    }
                }
            } catch (err) {
                console.warn("[LightShow] Error al obtener estado actual en late join:", err);
            }
        };

        fetchCurrentState();
        return () => {
            isCurrent = false;
        };
    }, [roomId]);

    const videoTrackReference = useRef(null); 
    const wakeLockReference = useRef(null);   
    const flashingIntervalReference = useRef(null); 

    // ==========================================
    // MÉTODO DE SALIDA DISCRETA Y LIMPIEZA DE HARDWARE
    // ==========================================
    const handleDiscreetExit = () => {
        if (window.confirm("¿Estás seguro de que deseas salir del show de luces?")) {
            // A) LIMPIEZA TOTAL: Cancelar cualquier frame o timer
            if (flashingIntervalReference.current) {
                clearInterval(flashingIntervalReference.current);
                flashingIntervalReference.current = null;
            }
            
            // B) APAGAR FLASH FÍSICO
            try {
                applyHardwareFlash(false);
            } catch (e) {}

            // C) DETENER EXPLICITAMENTE TODOS LOS TRACKS DE LA CÁMARA
            if (videoTrackReference.current) {
                try {
                    videoTrackReference.current.stop();
                } catch (e) {}
                videoTrackReference.current = null;
            }

            // D) LIBERACIÓN DE WAKELOCK
            if (window.activeWakeLock) {
                try {
                    window.activeWakeLock.release();
                } catch (e) {}
                window.activeWakeLock = null;
            }
            if (wakeLockReference.current) {
                wakeLockReference.current = null;
            }

            // E) RESTAURAR ESTILOS DEL DOM
            document.body.style.backgroundColor = '';
            const rootElement = document.getElementById("root");
            if (rootElement) rootElement.style.backgroundColor = '';
            const footerElement = document.getElementById("fdmFooter");
            if (footerElement) footerElement.style.backgroundColor = '';

            // F) DESCONEXIÓN Y REDIRECCIÓN
            if (onExit) {
                onExit();
            } else {
                localStorage.removeItem('user_zone');
                window.location.href = window.location.pathname + window.location.search;
            }
        }
    };

    // --- 1. ALGORITMO DE SINCRONIZACIÓN DE TIEMPO (Estilo SNTP) ---
    const syncTime = useCallback(async () => {
        setIsSyncing(true);
        const samples = [];
        const SAMPLES_NEEDED = 5;

        for (let i = 0; i < SAMPLES_NEEDED; i++) {
            try {
                const t0 = performance.now(); // Marca de tiempo de alta resolución local al enviar
                const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/time`);
                const serverTimeText = await response.text();
                const serverTime = parseInt(serverTimeText, 10); // Timestamp UNIX del servidor
                const t1 = performance.now(); // Marca de tiempo de alta resolución local al recibir
                const localRecv = Date.now(); // Timestamp UNIX local de recepción

                const rtt = t1 - t0; // Round Trip Time
                const latency = rtt / 2; // Latencia estimada unidireccional
                
                // Cálculo del desfase (Offset): HoraServidorEstimada - HoraCliente
                const offset = (serverTime + latency) - localRecv;
                samples.push({ offset, latency });
                
                // Intervalo pequeño entre muestras
                await new Promise(resolve => setTimeout(resolve, 100));
            } catch (err) {
                console.warn("Fallo en muestra de sincronización:", err);
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        }

        if (samples.length > 0) {
            // Ordenar muestras por latencia para usar las de menor RTT (las más estables)
            samples.sort((a, b) => a.latency - b.latency);
            const bestSamples = samples.slice(0, Math.min(3, samples.length));
            
            // Promediar los mejores offsets
            const avgOffset = bestSamples.reduce((sum, s) => sum + s.offset, 0) / bestSamples.length;
            setTimeOffset(avgOffset);
            console.log(`[Reloj HIFI] Sincronizado. Desfase promedio: ${Math.round(avgOffset)} ms`);
        }
        setIsSyncing(false);
    }, []);

    // Sincronización inicial al montar el componente
    useEffect(() => {
        syncTime();
    }, [syncTime]);

    // Función segura para solicitar bloqueo de pantalla (WakeLock)
    const requestWakeLock = async () => {
        try {
            if ('wakeLock' in navigator) {
                if (wakeLockReference.current) return; // ya activo
                wakeLockReference.current = await navigator.wakeLock.request('screen');
                window.activeWakeLock = wakeLockReference.current;
                console.log("[WakeLock] 🔓 Bloqueo de pantalla activado.");
                
                wakeLockReference.current.addEventListener('release', () => {
                    if (window.activeWakeLock === wakeLockReference.current) {
                        window.activeWakeLock = null;
                    }
                    wakeLockReference.current = null;
                    console.log("[WakeLock] 🔒 Bloqueo de pantalla liberado por el navegador.");
                });
            }
        } catch (err) {
            console.warn("[WakeLock] Error al solicitar WakeLock:", err);
        }
    };

    // Re-solicitar WakeLock y re-sincronizar reloj cuando la página vuelve a estar visible (en foco)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                syncTime();
                if (hasGrantedPermissions) {
                    requestWakeLock();
                }
            }
        };

        if (hasGrantedPermissions) {
            requestWakeLock();
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [hasGrantedPermissions, syncTime]);

    // 2. SOLICITUD DE PERMISOS (Cámara y WakeLock)
    // ==========================================
    const handleJoinShowClick = async () => {
        try {
            // Solicitar wake lock inicial
            await requestWakeLock();
            
            // Pedimos acceso a la cámara trasera para el Flash
            const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            videoTrackReference.current = mediaStream.getVideoTracks()[0];
            
            setHasGrantedPermissions(true);
        } catch (error) {
            console.warn("Permiso de cámara denegado o no disponible.");
            setHasGrantedPermissions(true); // Lo dejamos pasar para que disfrute el show en pantalla
            setPermissionError("Tu flash físico no encenderá, ¡pero tu pantalla sí!");
        }
    };

    // Solicitar permisos automáticamente si el show está corriendo y flashActive es true
    useEffect(() => {
        if (activeConfig && activeConfig.isRunning && !hasGrantedPermissions) {
            if (activeConfig.flashActive !== false) {
                console.log("[LightShow] Show activo detectado con flash habilitado. Solicitando permisos automáticamente.");
                handleJoinShowClick();
            } else {
                console.log("[LightShow] Show activo detectado con flash deshabilitado. Otorgando permisos automáticamente (solo pantalla).");
                setHasGrantedPermissions(true);
            }
        }
    }, [activeConfig?.isRunning, hasGrantedPermissions, activeConfig?.flashActive]);

    // ==========================================
    // 3. CONTROLADOR DE HARDWARE (Flash)
    // ==========================================
    const applyHardwareFlash = useCallback(async (turnOn) => {
        if (videoTrackReference.current) {
            try {
                // Modifica los "constraints" de la cámara para encender la linterna (torch)
                await videoTrackReference.current.applyConstraints({ advanced: [{ torch: turnOn }] });
            } catch (error) { 
                /* Ignorado silenciosamente para evitar bloquear el procesador si el hardware no responde rápido */ 
            }
        }
    }, []);

    // ==========================================
    // 4. EFECTOS DE COLOR GLOBAL DEL DOM
    // ==========================================
    useEffect(() => {
        // Pintamos los contenedores principales
        document.body.style.backgroundColor = activeBgColor;
        
        const rootElement = document.getElementById("root");
        if (rootElement) rootElement.style.backgroundColor = activeBgColor;
        
        const footerElement = document.getElementById("fdmFooter");
        if (footerElement) footerElement.style.backgroundColor = activeBgColor;
    }, [activeBgColor]);

    useEffect(() => {
        // Al desmontar por completo el componente, forzamos color vacío para regresar al blanco predeterminado
        return () => {
            document.body.style.backgroundColor = '';
            const rootElement = document.getElementById("root");
            if (rootElement) rootElement.style.backgroundColor = '';
            const footerElement = document.getElementById("fdmFooter");
            if (footerElement) footerElement.style.backgroundColor = '';
        };
    }, []);

    // Limpieza automática de hardware (cámara, flash y WakeLock) al desmontar naturalmente el componente
    useEffect(() => {
        return () => {
            if (flashingIntervalReference.current) {
                clearInterval(flashingIntervalReference.current);
                flashingIntervalReference.current = null;
            }
            try {
                applyHardwareFlash(false);
            } catch (e) {}
            if (videoTrackReference.current) {
                try {
                    videoTrackReference.current.stop();
                } catch (e) {}
                videoTrackReference.current = null;
            }
            if (window.activeWakeLock) {
                try {
                    window.activeWakeLock.release();
                } catch (e) {}
                window.activeWakeLock = null;
            }
            if (wakeLockReference.current) {
                wakeLockReference.current = null;
            }
        };
    }, [applyHardwareFlash]);

    // ==========================================
    // 5. EL MOTOR DE SINCRONIZACIÓN HIFI (rAF)
    // ==========================================
    useEffect(() => {
        const offColor = activeConfig?.offColor || '#000000';

        // Si el show no está activo, el usuario no ha dado permisos, o no hay colores configurados: Apagamos todo.
        if (!activeConfig?.isRunning || !hasGrantedPermissions || !activeConfig?.frames || activeConfig.frames.length === 0) {
            applyHardwareFlash(false);
            if (hasGrantedPermissions) {
                setActiveBgColor(offColor);
            } else {
                setActiveBgColor('');
            }
            return;
        }

        const beatsPerMinute = activeConfig.beatsPerMinute || 120;
        
        // Calculamos cuánto dura cada "Frame" en milisegundos.
        const millisecondsPerFrame = (60000 / beatsPerMinute) / (activeConfig.frames.length || 2); 
        
        // El reloj maestro del servidor
        const serverStartTimeAnchor = activeConfig.serverStartTimestampMilliseconds || Date.now();

        // Memoria local del bucle para no saturar hardware ni DOM
        let lastFrameColorApplied = null;
        let lastFrameFlashApplied = null;

        const isFlashActive = activeConfig.flashActive !== false;
        const isScreenActive = activeConfig.screenActive !== false;

        let animationFrameId = null;

        const loop = () => {
            // Calcular hora estimada actual en el servidor usando el desfase SNTP
            const estServerTime = Date.now() + timeOffset;
            const timeElapsedSinceStart = estServerTime - serverStartTimeAnchor;

            let targetColor = offColor;
            let targetFlash = false;

            if (timeElapsedSinceStart >= 0) {
                const absoluteFrameCount = Math.floor(timeElapsedSinceStart / millisecondsPerFrame);
                
                // Evaluamos qué frame toca mostrar en este instante
                const currentFrameIndexInArray = absoluteFrameCount % activeConfig.frames.length;
                const activeFrame = activeConfig.frames[currentFrameIndexInArray];

                targetColor = isScreenActive ? activeFrame.color : offColor;
                targetFlash = isFlashActive ? activeFrame.flashOn : false;
            }

            // A) ACTUALIZACIÓN DE PANTALLA (Solo si hay cambio)
            if (lastFrameColorApplied !== targetColor) {
                lastFrameColorApplied = targetColor;
                setActiveBgColor(targetColor);
            }

            // B) ACTUALIZACIÓN DE HARDWARE (FLASH) (Solo si hay cambio)
            if (lastFrameFlashApplied !== targetFlash) {
                lastFrameFlashApplied = targetFlash;
                applyHardwareFlash(targetFlash);
            }

            // Programar el siguiente renderizado de forma fluida a ~60-120 fps
            animationFrameId = requestAnimationFrame(loop);
        };

        // Lanzar el bucle de alta fidelidad
        animationFrameId = requestAnimationFrame(loop);

        // Función de limpieza cuando el componente se desmonta o el show se detiene
        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            applyHardwareFlash(false);
            if (hasGrantedPermissions) {
                setActiveBgColor(offColor);
            } else {
                setActiveBgColor('');
            }
        };
    }, [activeConfig?.isRunning, hasGrantedPermissions, activeConfig, applyHardwareFlash, timeOffset]);

    // ==========================================
    // 6. CÁLCULO DE CONTRASTE INTELIGENTE
    // ==========================================
    const getContrastColor = (hexColor) => {
        if (!hexColor || hexColor.trim() === '') return '#000000'; // Negro por defecto en fondo blanco
        
        let color = hexColor.trim();
        if (color.startsWith('#')) {
            color = color.slice(1);
        }
        if (color.length === 3) {
            color = color.split('').map(char => char + char).join('');
        }
        
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        
        if (isNaN(r) || isNaN(g) || isNaN(b)) return '#000000';
        
        // Fórmula de luminancia YIQ
        const yiq = (r * 299 + g * 587 + b * 114) / 1000;
        return yiq >= 128 ? '#000000' : '#FFFFFF';
    };

    const textColor = getContrastColor(activeBgColor);
    const isLightBg = textColor === '#000000';
    const softTextColor = isLightBg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255, 255, 255, 0.75)';

    // ==========================================
    // 7. RENDERIZADO DEL COMPONENTE
    // ==========================================
    return (
        <div 
            style={{ 
                backgroundColor: 'transparent', // Transparente para dejar ver el document.body modificado
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center',
                color: textColor,
                padding: '20px', 
                textAlign: 'center',
                touchAction: 'none', // Previene el zoom accidental (Doble Tap)
                userSelect: 'none',   // Previene seleccionar texto al dejar el dedo presionado
                transition: 'color 0.2s ease-in-out'
            }}
        >
            {/* VISTA 1: SOLICITUD DE PERMISOS */}
            {!hasGrantedPermissions && (
                <div className="p-4" style={{ maxWidth: '400px', backdropFilter: 'blur(10px)' }}>
                    {/* <i className="bi bi-phone-vibrate text-warning" style={{ fontSize: '5rem' }}></i> */}
                    {/* <img src='/img/page/xolo-mayor-show-de-luces.png' style={{ width: '85px' }} /> */}
                    <img src='/img/page/xolo-mayor-show-de-luces.png' style={{ width: '120px' }} />
                    <p className="mb-0 fs-4 fw-bold" style={{ fontSize: '1.1rem', color: isLightBg ? 'red' : 'red' }}>
                        ¡El estadio está a punto de brillar!
                    </p>

                    <button className="Btn__Adsl--Red mt-2" onClick={handleJoinShowClick}>Aceptar permiso para unirme</button>
                    
                    <p className="mb-4 mt-2" style={{ fontSize: '1.1rem', color: textColor }}>
                        Para unirte, necesitamos tu permiso para usar la linterna de tu smartphone.
                    </p>
                    
                    <p className="small mt-3 mb-0" style={{ color: softTextColor }}>
                        <i className="bi bi-shield-lock me-1"></i> Solo usamos tu linterna, no grabamos nada.
                    </p>

                    {/* Botón de escape discreto */}
                    <button 
                        type="button"
                        className="btn btn-outline-danger text-decoration-none mt-3" 
                        style={{ color: isLightBg ? '#dc3545' : '#ffc107', fontSize: '0.9rem', display: 'block', margin: '0 auto', pointerEvents: 'auto' }} 
                        onClick={handleDiscreetExit}
                    >
                        Salir del show de luces
                    </button>
                </div>
            )}

            {/* VISTA 2: PERMISOS OTORGADOS, EN ESPERA DEL ADMIN */}
            {hasGrantedPermissions && !activeConfig?.isRunning && (
                <div className="p-4" style={{ backdropFilter: 'blur(5px)' }}>
                    {/* <i className="bi bi-check-circle-fill mb-3" style={{ fontSize: '4.5rem', color: isLightBg ? '#198754' : '#4ade80' }}></i> */}
                    <img src='/img/page/xolo-mayor-show-de-luces.png' style={{ width: '140px' }} />
                    <h2 className="font-weight-bold mb-2" style={{ color: textColor }}>¡Estás conectad@!</h2>
                    <p className="mb-0 small fw-bold" style={{ color: isLightBg ? '#dc3545' : '#f87171' }}>No salgas de esta pantalla</p>
                    <p className="fs-5 mt-2" style={{ color: textColor }}>El show está a punto de comenzar...</p>
                    {isSyncing ? (
                        // <p className="small mt-2" style={{ color: softTextColor }}>📡 Calibrando precisión temporal...</p>
                        <p className="small mt-2" style={{ color: softTextColor }}>📡 Ya casi...</p>
                    ) : (
                        // <p className="small mt-2" style={{ color: softTextColor }}>⚡ Precisión sincronizada</p>
                        <p className="small mt-2" style={{ color: softTextColor }}>⚡</p>
                    )}
                    {permissionError && (
                        <div className="alert alert-warning mt-4 small mb-0 text-start" style={{ 
                            backgroundColor: isLightBg ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 193, 7, 0.1)',
                            color: textColor,
                            borderColor: 'rgba(255, 193, 7, 0.4)'
                        }}>
                            <i className="bi bi-info-circle-fill me-1"></i> {permissionError}
                        </div>
                    )}
                    {/* Botón de escape discreto */}
                    <button 
                        type="button"
                        className="btn btn-outline-danger text-decoration-none mt-4" 
                        style={{ color: isLightBg ? '#dc3545' : '#ffc107', fontSize: '0.9rem', display: 'block', margin: '0 auto', pointerEvents: 'auto' }} 
                        onClick={handleDiscreetExit}
                    >
                        Salir del show de luces
                    </button>
                </div>
            )}

            {/* VISTA 3: SHOW CORRIENDO */}
            {hasGrantedPermissions && activeConfig?.isRunning && (
                <div className="p-4" style={{ backdropFilter: 'blur(5px)' }}>
                    <h2 className="font-weight-bold mb-0" style={{ color: textColor }}>¡Disfruta el show!</h2>
                    <img src='/img/page/xolo-mayor-show-de-luces.png' style={{ width: '180px' }} />
                    <p className="mb-0 small fw-bold" style={{ color: isLightBg ? '#dc3545' : '#f87171' }}>No salgas de esta pantalla</p>
                    {/* Botón de escape discreto */}
                    <button 
                        type="button"
                        className="btn btn-link text-decoration-none mt-4" 
                        style={{ color: isLightBg ? '#dc3545' : '#ffc107', fontSize: '0.85rem', opacity: 0.8, display: 'block', margin: '0 auto', pointerEvents: 'auto' }} 
                        onClick={handleDiscreetExit}
                    >
                        Cerrar Sesión / Salir
                    </button>
                </div>
            )}
        </div>
    );
};

export { LightShow };